/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useMemo, useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

import {
  ApolloError,
  Divider,
  Heading,
  HStack,
  LoadingIndicator,
  ScrollPane,
  SearchInput,
  Spacer,
  TableView,
  Text,
  VStack,
} from '../../core-ui';
import formatDate from '../../core/support/formatDate';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';
import { objectMap } from '../../support/objectMap';
import { APP_STATUSES } from '../../support/constants/AppStatuses';
import { MarketDates } from '../../types/shared/Vendor';
import {
  Button,
  BackLink,
  Redirect,
  useNavigation,
  useParams,
} from '../Navigation';
import { TagView } from '../Vendors/VendorTag';
import { useSeason } from '../SeasonState';

import MarketDatesFilterModal from './MarketDatesFilterModal';
import CheckboxListModal from './CheckboxListModal';
import {
  GetRecipients,
  GetRecipients_season_vendors as VendorType,
  GetRecipients_season_markets as Market,
  GetRecipients_season_tags as Tag,
  GetRecipientsVariables,
} from './__generated__/GetRecipients';
import { CreateJob, CreateJobVariables } from './__generated__/CreateJob';
import { StatusBadge } from '../Vendors/VendorStatus';
import {
  getCategoryOptionsFromGraphQLFields,
  OptionsMap,
} from '../../support/helpers/CategoryOptions';
import { toMap } from '../../support/helpers/toMap';
import { Option } from '../../runtypes/SignupFormDefinitionRuntype';

const GET_RECIPIENTS = gql`
  query GetRecipients($emailId: String!, $seasonId: String!) {
    email(id: $emailId) {
      id
      subject
      job {
        id
      }
      status
    }
    season(id: $seasonId) {
      id
      signupForm {
        fields {
          id
          type
          definition
        }
      }
      vendors {
        id
        name
        contact
        email
        category
        status
        tags {
          id
        }
        markets {
          id
          dates
        }
        createdAt
        updatedAt
      }
      markets {
        id
        name
        description
        dates
        numSpots
        hidden
        createdAt
        updatedAt
      }
      tags {
        id
        name
        color
      }
    }
  }
`;

const CREATE_JOB = gql`
  mutation CreateJob($emailId: String!, $recipients: [String!]!) {
    createJob(data: { emailId: $emailId, recipients: $recipients }) {
      id
    }
  }
`;

type Vendor = Omit<VendorType, '__typename'>;

type Props = {
  emailId: string;
  optionsMap: OptionsMap;
  vendors: Array<Vendor>;
  markets: Array<Market>;
  tags: Array<Tag>;
  vendorCategories: Array<Option>;
};

function SelectRecipientsPage(props: Props) {
  const {
    emailId,
    optionsMap,
    vendors,
    markets,
    tags,
    vendorCategories,
  } = props;
  const { navigate } = useNavigation();

  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set<string>());
  const [selectedMarketDates, setSelectedMarketDates] = useState<MarketDates>(
    {},
  );
  const [selectedCategories, setSelectedCategories] = useState(
    new Set<string>(),
  );
  const [selectedStatuses, setSelectedStatuses] = useState(new Set<string>());
  const [sortBy, setSortBy] = useState<[number, boolean]>(
    // Initially sort by column 0 (name) ascending
    [0, false],
  );

  const showApolloError = useShowApolloError();

  const [createJob, { loading: isSubmitting }] = useMutation<
    CreateJob,
    CreateJobVariables
  >(CREATE_JOB, {
    onError: showApolloError,
    onCompleted: (data) => {
      const id = data.createJob?.id ?? '';
      navigate('/mailer/jobs/:id/preview', { id });
    },
  });

  const filteredVendors = useMemo(() => {
    return filterVendors(vendors, {
      searchText,
      selectedCategories,
      selectedStatuses,
      selectedMarketDates,
    });
  }, [
    vendors,
    searchText,
    selectedCategories,
    selectedStatuses,
    selectedMarketDates,
  ]);

  const tagMap = new Map(tags.map((tag) => [tag.id, tag]));

  return (
    <VStack align="stretch" flex={1} minH={0} px={10} pt={2}>
      <BackLink
        to={['/mailer/emails/:id/edit', { id: emailId }]}
        alignSelf="start"
      />
      <HStack>
        <Heading as="h2" size="lg">
          {t('Select Recipients')}
        </Heading>
        <Spacer />
        <Button
          isDisabled={selectedIds.size === 0}
          onClick={() => {
            createJob({
              variables: {
                emailId: emailId,
                recipients: Array.from(selectedIds),
              },
            });
          }}
          variant="primary"
          minW={170}
          isLoading={isSubmitting}
        >
          {t('Send to {count} vendor(s)', { count: selectedIds.size })}
        </Button>
      </HStack>
      <Divider borderColor="gray.200" />
      <HStack>
        <SearchInput
          onChange={(event) => setSearchText(event.target.value)}
          onClear={() => setSearchText('')}
          value={searchText}
        />
        <MarketDatesFilterModal
          markets={markets}
          initialSelection={selectedMarketDates}
          onSubmit={(value) => setSelectedMarketDates(value)}
        />
        <CheckboxListModal
          title={t('Category')}
          items={vendorCategories ?? []}
          submitButtonText={t('Set Filter')}
          onSubmit={(selected) =>
            setSelectedCategories(
              new Set(Array.from(selected).map((item) => item.id)),
            )
          }
          renderLabel={(item) => item.name}
        />
        <CheckboxListModal
          title={t('Status')}
          items={APP_STATUSES}
          submitButtonText={t('Set Filter')}
          onSubmit={(selected) => setSelectedStatuses(selected)}
        />
      </HStack>
      <ScrollPane contentContainerProps={{ pb: 10 }}>
        <TableView
          data={filteredVendors}
          selected={selectedIds}
          onSelectionChange={(selectedIds) => {
            setSelectedIds(selectedIds);
          }}
          onRowClick={(vendor) => {
            const { id } = vendor;
            const selection = new Set(selectedIds);
            selection.has(id) ? selection.delete(id) : selection.add(id);
            setSelectedIds(selection);
          }}
          sortBy={sortBy}
          onSortChange={setSortBy}
          columns={[
            {
              title: t('Name'),
              render: (vendor) => vendor.name,
              tdProps: { maxWidth: 300 },
              sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
            },
            {
              title: t('Contact'),
              render: (vendor) => vendor.contact,
              tdProps: { maxWidth: 300 },
              sortable: {
                sortFn: (a, b) => a.contact.localeCompare(b.contact),
              },
            },
            {
              title: t('Category'),
              render: (vendor) =>
                vendor.category
                  .map((id) => optionsMap.get(id)?.name ?? '')
                  .join(', '),
            },
            {
              title: t('Signup Date'),
              render: (vendor) => formatDate(vendor.createdAt),
              tdProps: { whiteSpace: 'nowrap' },
              sortable: {
                sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
              },
            },
            {
              title: t('Status'),
              render: (vendor) => <StatusBadge status={vendor.status} />,
              sortable: { sortFn: (a, b) => a.status.localeCompare(b.status) },
            },
            {
              title: t('Tags'),
              render: (vendor) => (
                <HStack spacing={1}>
                  {vendor.tags.map(({ id }) => {
                    const tag = tagMap.get(id);
                    return tag ? <TagView key={id} tag={tag} /> : null;
                  })}
                </HStack>
              ),
            },
          ]}
        />
      </ScrollPane>
    </VStack>
  );
}

function filterVendors(
  vendors: Array<Vendor>,
  filters: {
    searchText: string;
    selectedCategories: Set<string>;
    selectedStatuses: Set<string>;
    selectedMarketDates: MarketDates;
  },
) {
  const { selectedCategories, selectedStatuses } = filters;
  const selectedMarketDates = objectMap(
    filters.selectedMarketDates,
    (id, dates) => new Set(dates),
  );
  const numSelected = Object.values(selectedMarketDates).reduce(
    (total, set) => total + set.size,
    0,
  );
  const searchText = filters.searchText.toLowerCase().trim();
  return vendors.filter(
    (vendor) =>
      matchesCategories(vendor, selectedCategories) &&
      matchesStatuses(vendor, selectedStatuses) &&
      matchesMarketDates(vendor, numSelected, selectedMarketDates) &&
      matchesSearch(vendor, searchText),
  );
}

function matchesCategories(vendor: Vendor, selectedCategories: Set<string>) {
  if (selectedCategories.size === 0) {
    return true;
  }
  return vendor.category.some((categoryId) =>
    selectedCategories.has(categoryId),
  );
}

function matchesStatuses(vendor: Vendor, selectedStatuses: Set<string>) {
  if (selectedStatuses.size === 0) {
    return true;
  }
  return selectedStatuses.has(vendor.status);
}

function matchesMarketDates(
  vendor: Vendor,
  numSelected: number,
  selectedMarketDates: ObjectOf<Set<string>>,
) {
  if (numSelected === 0) {
    return true;
  }
  for (const { id, dates } of vendor.markets) {
    const selectedDates = selectedMarketDates[id];
    if (selectedDates) {
      for (const date of dates) {
        if (selectedDates.has(date)) {
          return true;
        }
      }
    }
  }
  return false;
}

function matchesSearch(vendor: Vendor, searchText: string) {
  if (searchText === '') {
    return true;
  }
  for (const key of ['id', 'name', 'contact', 'email'] as const) {
    const value = typeof vendor[key] === 'string' ? vendor[key] : '';
    if (value.toLowerCase().includes(searchText)) {
      return true;
    }
  }
  return false;
}

export default function SelectRecipientsPageContainer() {
  const params = useParams();
  const { season } = useSeason();
  const seasonId = season?.id ?? '';
  const emailId = params.id ?? '';
  const { error, data } = useQuery<GetRecipients, GetRecipientsVariables>(
    GET_RECIPIENTS,
    {
      variables: { seasonId, emailId },
      fetchPolicy: 'cache-and-network',
    },
  );
  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.email) {
    // TODO: Make a Not Found component
    return <Text>{t('Not Found')}</Text>;
  }
  const { email } = data;
  const vendors = data.season?.vendors ?? [];
  const markets = data.season?.markets ?? [];
  const tags = data.season?.tags ?? [];
  const options = getCategoryOptionsFromGraphQLFields(
    data?.season?.signupForm?.fields ?? [],
  );
  const optionsMap = toMap(options);
  if (email.status === 'PENDING') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/preview', { id: jobId }]} />;
  }
  if (email.status === 'SENDING') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/progress', { id: jobId }]} />;
  }
  if (email.status === 'SENT') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/summary', { id: jobId }]} />;
  }
  return (
    <SelectRecipientsPage
      emailId={emailId}
      optionsMap={optionsMap}
      vendors={vendors}
      markets={markets}
      tags={tags}
      vendorCategories={options}
    />
  );
}
